import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, CHAT_RESOLVERS, WS_MESSAGE_TYPE } from 'cpool-constants';

import { handleInboxSocketMessage } from '../utils/handleInboxSocketMessageEvent';
import { invalidateInboxUnreadQueries } from '../utils/invalidateInboxUnreadQueries';
import { wsClient } from '~/api/wsClient';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useUserInfo from '~/hooks/useUserInfo';
import { queryKeys } from '~/utils/queryKeys';

// App-shell-level inbox handler — mounted once so the header badge stays live regardless of panel state.
export function useInboxSocket(): void {
    const queryClient = useQueryClient();
    const user = useUserInfo();
    const { permissions } = useAclPermissions();
    // Roles with no CHAT grant (corporationDirector/accountantManager/emailManager) never register this handler.
    const hasChatAccess = permissions.resolvers[AGGREGATE_NAMES.CHAT][CHAT_RESOLVERS.getInboxUnreadSummary];

    useEffect(() => {
        if (!user || !hasChatAccess) return undefined;

        wsClient.registerHandler(WS_MESSAGE_TYPE.CHAT_INBOX, {
            onMessage: (message) => {
                handleInboxSocketMessage(message, queryClient, user.id);
            },
            // Resync on (re)connect — pushes during socket downtime are lost.
            onOpen: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.getChats() });
                invalidateInboxUnreadQueries(queryClient, user.id);
            },
        });

        return () => {
            wsClient.unregisterHandler(WS_MESSAGE_TYPE.CHAT_INBOX);
        };
    }, [hasChatAccess, queryClient, user]);
}
